import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { css, cx } from 'emotion';
import { Button } from 'antd';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ModalsService from 'services/ModalsService';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import OutputDatastreamNameField from 'components/core/fields/OutputDatastreamNameField';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import RIECheckboxSelect from 'components/core/editable/RIECheckboxSelect';
import ProcessWizardPage from 'components/page/process/wizard/ProcessWizardPage';

import OutputDatastreamsField from 'components/core/fields/OutputDatastreamsField';
import { colorBorderGrey } from 'styles/base';

export default class OutputPage extends ProcessWizardPage {
    static defaultProps = {
        title: 'Setup Output',
        description: 'Specify the properties of the consolidated output datastream.'
    };

    getAllowableDimensions = () => {
        return ProcessUtils.getOutputDimensionsList(this.props.process).filter(dimension => {
            // not allowed to rename time dimension
            return dimension !== 'time';
        });
    };

    onOutputIntervalUpdated = (fieldPath, value) => {
        ProcessUtils.updateField(this.props.process, fieldPath, value);
    };

    getIntervalOptions = () => {
        const options = ['Daily'];
        const selectOptions = [];
        options.forEach((option) => {
            let value = 0;
            switch (option) {
                case 'Daily':
                    value = 86400;
                    break;
                case 'Monthly':
                    // TODO: this isn't actually monthly (this is 4 weeks)
                    value = 2419200;
                    break;
                case 'Yearly':
                    // TODO: this isn't actually yearly (this is 52 weeks)
                    value = 3144960;
                    break;
                default:
                    value = 0;
                    break;
            }
            selectOptions.push({ value, label: option });
        });

        return selectOptions;
    };

    dimensionsHaveChanged = (inputDimensions, outputDimensions) => {
        const diff = inputDimensions.filter(i => {
            return outputDimensions.indexOf(i) < 0;
        });

        return diff.length > 0;
    };

    onRenameDims = () => {
        if (this.getAllowableDimensions().length === 0) {
            // if the only dimension is time, then this button is disabled
            return;
        }
        let formComponent = null;
        const formRefFunction = (component) => { formComponent = component; };

        const form = (
            <RenameDimensionsForm
                process={this.props.process}
                dimensions={this.getAllowableDimensions()}
                ref={formRefFunction}
            />
        );

        const onOk = () => {
            let ret = true;
            const values = formComponent.getFormValues();
            const newName = values.name;
            const renameDimensions = values.selectedDimensions;

            const valid = formComponent.validateAll();

            if (valid) {
                const editor = ProcessUtils.getEditor(this.props.process);

                ProcessUtils.getOutputDimensionsList(this.props.process).forEach(currentDim => {
                    if (renameDimensions.indexOf(currentDim) > -1 && newName !== currentDim) {
                        editor.renameDimension(ProcessUtils.getWizardCoordinateSystemName(this.props.process), currentDim, newName);
                    }
                });
                editor.commit();
            } else {
                ret = false;
            }

            return ret;
        };

        ModalsService.openModal({
            id: 'rename_dimensions',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Rename Dimensions',
            modalMessage: form,
            onOk
        });
    };

    onResetDims = () => {
        const editor = ProcessUtils.getEditor(this.props.process);

        ProcessUtils.visitRetrievedVariables(this.props.process, (retrievedVariable) => {
            const dims = ProcessUtils.getRetrievedVariableDimensionsFromInputstream(this.props.process, retrievedVariable.name);
            editor.updateField(['variable_retrieval', 'retrieved_variables', retrievedVariable.name, 'dims'], dims);
        });

        let inputList = this.getInputDimensionsList();
        if (inputList.indexOf('time') === -1) {
            inputList = ['time', ...inputList];
        }

        const outputList = ProcessUtils.getOutputDimensionsList(this.props.process);
        const coordinateSystemName = ProcessUtils.getWizardCoordinateSystemName(this.props.process);
        outputList.forEach(outputDimension => {
            if (inputList.indexOf(outputDimension) === -1) {
                editor.removeDimension(coordinateSystemName, outputDimension);
            }
        });
        inputList.forEach(inputDimension => {
            if (outputList.indexOf(inputDimension) === -1) {
                editor.initializeDimension(coordinateSystemName, inputDimension); // defaults to mapping grid shape
            }
        });

        editor.commit();
    };

    getOutputVariables = () => {
        const list = [];
        ProcessUtils.visitRetrievedVariables(this.props.process, (retrievedVariable) => {
            list.push(retrievedVariable.name);
        });

        return list;
    };

    renderOutputVariablesList = () => {
        const list = this.getOutputVariables().sort();

        return list.map((variableName) => {
            const dimensions = ProcessUtils.getRetrievedVariableDimensions(this.props.process, variableName);
            const units = ProcessUtils.getRetrievedVariableUnits(this.props.process, variableName);
            return (
                <div key={variableName} className="list-item">
                    {variableName}
                    <span className="sub-text">{dimensions}: {units}</span>
                </div>
            );
        });
    };

    renderOutputDatastreamsForm = () => {
        return (
            <OutputDatastreamsField
                process={this.props.process}
                processErrors={this.props.processErrors}
                dods={this.props.dods}
            />
        );
    };

    renderOutputsForm() {
        const intervalOptions = this.getIntervalOptions();
        let intervalValue = { value: null, label: null };
        intervalOptions.forEach(option => {
            if (option.value === parseInt(this.props.process.processing_interval, 10)) {
                intervalValue = { value: option.value, label: option.label };
            }
        });

        const outputFormStyles = css`
            width:100%;
            div.infobox {
                min-width: 200px;
                align-self: flex-start !important;
            }
            .editable-component:not(.editing) {
                padding-right: 0 !important;
            }
            .field-margin {
                margin-bottom: 10px;
            }
            .output-vars-list {
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 100%;
                background-color: #fff;
                border: 1px solid ${colorBorderGrey};
                padding: 5px 10px;
                margin-top: 5px;
                .list-item {
                    padding: 3px 0;
                }
                .list-item:hover {
                    cursor: inherit !important;
                }
            }
        `;

        const inputDimensions = this.getInputDimensionsList();
        const outputDimensions = ProcessUtils.getOutputDimensionsList(this.props.process);
        const renameDisabled = this.getAllowableDimensions().length === 0 || !this.props.hasWritePermissions;

        return (
            <Flexbox flexDirection="column" className={cx('output-form', outputFormStyles)}>
                <OutputDatastreamNameField
                    value={this.getOutputDatastreamName()}
                    process={this.props.process}
                    dods={this.props.dods}
                    processErrors={this.props.processErrors}
                    forceFormDisplay
                    navigateOnRename={false}
                    dodOnTheFlyOnly
                    className={cx('field-margin', css`flex:0 0 auto;`)}
                    hasWritePermissions={this.props.hasWritePermissions}
                />
                <Flexbox className="field-margin" flexDirection="row" alignItems="flex-start" flex="0 0 auto">
                    <InfoBoxLabel
                        label="Output File Interval"
                    />
                    <RIEComboBox
                        value={intervalValue}
                        change={this.onOutputIntervalUpdated}
                        fieldPath={['processing_interval']}
                        isDisabled={!this.props.hasWritePermissions}
                        options={intervalOptions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        formDisplay
                        className={css`padding-left: 0 !important;`}
                    />
                </Flexbox>
                <Flexbox className={cx('field-margin', css`position: relative;`)} flexDirection="row" alignItems="flex-start" flex="0 0 auto">
                    <InfoBoxLabel
                        label="Output Dimensions"
                    />
                    <Flexbox className={css`margin: 0 10px 0 3px; height: 30px;`}>
                        {ProcessUtils.getDimensionsAsString(outputDimensions).fullList}
                    </Flexbox>
                    <Flexbox className={css`position:absolute; right:0;`}>
                        {ComponentUtils.renderPermissionDependentComponent(
                            <Button onClick={this.onRenameDims} disabled={renameDisabled}
                                    className={css`margin:0 3px !important;`}>
                                Rename Dimensions
                            </Button>,
                            this.props.hasWritePermissions)
                        }
                        {this.dimensionsHaveChanged(inputDimensions, outputDimensions) &&
                        <Button onClick={this.onResetDims} className={css`margin:0 3px !important;`}>
                            Reset Dimensions
                        </Button>
                        }
                    </Flexbox>
                </Flexbox>
                <Flexbox className="field-margin" flexDirection="column" alignItems="stretch" flex="1">
                    <InfoBoxLabel
                        label="Output Variables"
                        infoIcon={false}
                    />
                    <Flexbox className="output-vars-list" flexDirection="column" flex="1">
                        {this.renderOutputVariablesList()}
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }

    renderFormWithProcess() {
        return this.renderOutputsForm();
    }
}

class RenameDimensionsForm extends React.Component {
    static propTypes = {
        dimensions: PropTypes.array,
        process: PropTypes.object
    };

    constructor(props) {
        super(props);

        // If there is only one dimension, then we will default it to checked
        let selectedDimensions = [];
        if (props.dimensions.length >= 1) {
            selectedDimensions = [props.dimensions[0]];
        }

        this.state = {
            selectedDimensions,
            name: null,
            validation: {
                name: {
                    valid: true,
                    message: ''
                },
                selectedDimensions: {
                    valid: true,
                    message: ''
                }
            }
        };
    }

    getFormValues = () => {
        return { name: this.state.name, selectedDimensions: this.state.selectedDimensions };
    };

    getDimensionSelections = () => {
        return this.props.dimensions.map(dimension => {
            return { key: dimension, label: dimension, checked: this.state.selectedDimensions.indexOf(dimension) > -1 };
        });
    };

    onDimensionSelectionChanged = (fieldPath, value) => {
        const selectedDimensions = [];
        value.forEach(checkbox => {
            if (checkbox.checked) {
                selectedDimensions.push(checkbox.key);
            }
        });
        const validation = this.validateSelections(this.state.validation, selectedDimensions);
        this.setState({ selectedDimensions, validation });
    };

    onNameChanged = (fieldPath, value) => {
        const validation = this.validateName(this.state.validation, value.value);
        this.setState({ name: value.value, validation });
    };

    validateSelections = (validation, selections) => {
        const newValidation = Object.assign({}, validation);
        let valid = true;
        let message = '';

        if (selections.length === 0) {
            valid = false;
            message = 'Must select at least one dimension to rename';
        }
        newValidation.selectedDimensions = { valid, message };

        return newValidation;
    };

    validateName = (validation, name) => {
        const newValidation = Object.assign({}, validation);
        let valid = true;
        let message = '';

        const nameValidation = ProcessUtils.getValidator(this.props.process).isEntityNameValid(name, null, 'Dimension name');
        if (nameValidation.level !== null) {
            valid = false;
            message = nameValidation.message;
        }
        newValidation.name = { valid, message };

        return newValidation;
    };

    validateAll = () => {
        let valid = true;
        let validation = Object.assign({}, this.state.validation);
        validation = this.validateSelections(validation, this.state.selectedDimensions);
        validation = this.validateName(validation, this.state.name);

        if (!validation.name.valid || !validation.selectedDimensions.valid) {
            valid = false;
        }

        this.setState({ validation });
        return valid;
    };

    renderError(message) {
        return (<span><i className={`fa fa-exclamation-triangle ${css`padding-right: 5px;`}`}/>{message}</span>);
    }

    render() {
        const errorMessageRefFunction = (component) => {this.errorMessageComponent = component;};

        const nameValue = Constants.convertValueToSelectValue(this.state.name);
        const nameOptions = Constants.convertArrayToSelectOptions(this.props.dimensions);

        return (
            <Flexbox
                flexDirection="column"
                className={css`div.infobox{ min-width: 160px; }`}
            >
                <Flexbox flexDirection="row" className={css`margin-bottom:10px;`}>
                    <InfoBoxLabel
                        label="Dimensions to rename"
                        infoIcon={false}
                    />
                    <RIECheckboxSelect
                        value={this.getDimensionSelections()}
                        change={this.onDimensionSelectionChanged}
                        fieldPath={null}
                        vertical
                    />
                </Flexbox>
                <Flexbox flexDirection="row">
                    <InfoBoxLabel
                        label="Dimension name"
                        infoIcon={false}
                    />
                    <RIEComboBox
                        value={nameValue}
                        change={this.onNameChanged}
                        fieldPath={null}
                        isDisabled={false}
                        options={nameOptions}
                        formDisplay
                        creatable
                    />
                </Flexbox>
                <Flexbox flexDirection="column" className="invalid" style={{ marginTop: '15px' }} ref={errorMessageRefFunction}>
                    {!this.state.validation.selectedDimensions.valid && this.renderError(this.state.validation.selectedDimensions.message)}
                    {!this.state.validation.name.valid && this.renderError(this.state.validation.name.message)}
                </Flexbox>
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/wizard/OutputPage.js